import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useDictation } from "@/hooks/use-dictation";
import { Waveform } from "./Waveform";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mic, Sparkles, Square, Trash2, Keyboard } from "lucide-react";
import "./field.css";

const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * VoiceCapture – «snakk inn rapporten». Stor mikrofon, ekte bølgeform, live transkript ord for ord, og når du stopper:
 * redigerbar tekst + «Strukturer med KI» (fyller skjemaet). Fungerer også uten tale (skriv i stedet) og sier tydelig fra
 * når nettleseren ikke støtter diktering eller mikrofonen er blokkert.
 *
 * value/onChange er den rå teksten (eies av forelderen). onStructure() kalles når brukeren vil at KI skal strukturere.
 */
export function VoiceCapture({ value, onChange, onStructure, structuring = false, canStructure = true, title = "Fortell hva som har skjedd", hint = "Snakk inn – vi gjør det om til et strukturert avvik.", placeholder = "Hva, hvor, konsekvens og hva som bør gjøres …", structureLabel = "Strukturer med KI", testId = "voice-capture", className }) {
  const d = useDictation({ lang: "nb-NO" });
  const [typing, setTyping] = useState(false);
  const [sec, setSec] = useState(0);
  const lastPushed = useRef(value);

  // dikteringstekst → forelder (kun når den faktisk endrer seg, så brukerens egne redigeringer ikke blir overskrevet)
  useEffect(() => {
    if (d.text !== lastPushed.current) { lastPushed.current = d.text; onChange(d.text); }
  }, [d.text, onChange]);
  // forelder tømmer/overskriver → synk inn i hooken
  useEffect(() => {
    if (value !== lastPushed.current) { lastPushed.current = value; d.setText(value || ""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  useEffect(() => {
    if (!d.listening) return undefined;
    setSec(0);
    const t = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [d.listening]);

  const hasText = !!(value && value.trim());
  const showEditor = !d.listening && (hasText || typing || !d.supported);
  const showLive = d.listening;

  return (
    <section className={cn("field-card p-4 space-y-3", className)} data-testid={testId} data-listening={d.listening ? "true" : "false"} data-supported={d.supported ? "true" : "false"}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[15px] font-semibold leading-tight flex items-center gap-2"><Mic className="h-4 w-4 text-primary" /> {title}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
        </div>
        {d.listening && <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-muted-foreground tabular" data-testid={`${testId}-timer`}><i className="voice-rec-dot" /> {fmtSec(sec)}</span>}
      </div>

      {d.supported && (
        <div className="flex flex-col items-center gap-2.5 py-1">
          <button type="button" className="voice-mic" data-listening={d.listening ? "true" : "false"} onClick={d.toggle} aria-pressed={d.listening} aria-label={d.listening ? "Stopp opptak" : "Start diktering"} data-testid={`${testId}-mic`}>
            <span className="voice-mic-ring" /><span className="voice-mic-ring" /><span className="voice-mic-ring" />
            {d.starting ? <Loader2 className="h-6 w-6 animate-spin" /> : d.listening ? <Square className="h-6 w-6 fill-current" /> : <Mic className="h-7 w-7" />}
          </button>
          <Waveform active={d.listening} />
          <div className="text-xs text-muted-foreground" data-testid={`${testId}-status`} data-state={d.status}>
            {d.starting ? "Kobler til mikrofonen …" : d.listening ? "Lytter – trykk for å stoppe" : hasText ? "Trykk for å fortsette å snakke" : "Trykk og fortell – på norsk, som til en kollega"}
          </div>
        </div>
      )}

      {showLive && (
        <div className="rounded-xl border bg-muted/30 px-3 py-2.5 text-[15px] leading-relaxed min-h-[72px]" aria-live="polite" data-testid={`${testId}-live`}>
          {value}{value && d.interim ? " " : ""}<span className="voice-interim">{d.interim}</span><span className="voice-caret" />
          {!value && !d.interim && <span className="text-muted-foreground">…</span>}
        </div>
      )}

      {showEditor && (
        <Textarea rows={3} value={value || ""} onChange={(e) => { lastPushed.current = e.target.value; d.setText(e.target.value); onChange(e.target.value); }} placeholder={placeholder} className="voice-textarea text-[15px] leading-relaxed" data-testid={`${testId}-text`} />
      )}

      {!d.supported && (
        <div className="rounded-xl border border-dashed px-3 py-2 text-xs text-muted-foreground" data-testid={`${testId}-unsupported`}>Denne nettleseren støtter ikke diktering. Bruk Safari eller Chrome på mobilen – eller skriv teksten over.</div>
      )}
      {d.error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200" data-testid={`${testId}-error`}>{d.error.message}</div>
      )}

      {!d.listening && (hasText || typing || !d.supported) && (
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => { d.reset(); lastPushed.current = ""; onChange(""); setTyping(false); }} disabled={!hasText || structuring} aria-label="Tøm teksten" data-testid={`${testId}-clear`}><Trash2 className="h-4 w-4" /></Button>
          {canStructure ? (
            <Button type="button" className="h-11 flex-1" onClick={onStructure} disabled={structuring || !hasText || value.trim().length < 8} data-testid={`${testId}-structure`}>
              {structuring ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} {structuring ? "Strukturerer …" : structureLabel}
            </Button>
          ) : (
            <div className="text-xs text-muted-foreground">Teksten er klar – fyll inn feltene under.</div>
          )}
        </div>
      )}
      {!d.listening && !hasText && !typing && d.supported && (
        <button type="button" onClick={() => setTyping(true)} className="mx-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid={`${testId}-type-instead`}><Keyboard className="h-3.5 w-3.5" /> Skriv i stedet</button>
      )}
    </section>
  );
}
